import { useMemo, useRef } from 'react';
import type { MutableRefObject } from 'react';
import { Gesture, type ComposedGesture } from 'react-native-gesture-handler';
import type { PhotoZoomViewport } from './photoZoomViewport';
import { useEventCallback } from './useEventCallback';

type PhotoZoomGestureOptions = {
  /** Текущее состояние — через ref: жест читает его на каждый кадр, пересобирать нельзя. */
  viewportRef: MutableRefObject<PhotoZoomViewport>;
  /** Куда отдать новое состояние; отсечение границ — забота вызывающего. */
  apply: (next: PhotoZoomViewport) => void;
  /** Перетаскивание. У листалки пачки выключено, пока снимок вписан: жест принадлежит ей. */
  panEnabled?: boolean;
  /** Щипок и двойное касание. Выключены, пока неизвестны размеры кадра. */
  zoomEnabled?: boolean;
  /** Что делает двойное касание. Не задано — двойного касания нет вовсе. */
  onDoubleTap?: (current: PhotoZoomViewport) => PhotoZoomViewport;
  /** Одиночное касание; вызывается только у невприближённого снимка (см. ниже). */
  onSingleTap?: () => void;
};

/**
 * Щипок, перетаскивание и касания для просмотра снимка.
 *
 * <p>Собран из четырёх почти одинаковых копий: просмотр фото профиля, редактор аватара,
 * просмотрщик снимков в переписке и редактор кадра. Совпадало всё, кроме мелочей — куда прыгает
 * двойное касание и когда жесты включены, — а вот две неочевидные вещи каждая копия помнила
 * сама и могла однажды забыть:
 *
 * <ul>
 *   <li><b>{@code runOnJS(true)} обязателен.</b> При установленном Reanimated колбэки
 *       gesture-handler становятся worklet-ами, а состояние здесь обычное React-ное — без этого
 *       жест молча ничего не двигает.</li>
 *   <li><b>{@code Race}, а не {@code Exclusive}, между тапами и перетаскиванием.</b> Exclusive
 *       заставил бы перетаскивание ждать таймаут распознавания двойного касания: палец уже
 *       поехал, а картинка стоит.</li>
 * </ul>
 *
 * <p>Редактор кадра ({@code PhotoCropEditor}) сюда намеренно не переехал: у него своё состояние
 * с углом поворота, и дельту перетаскивания приходится поворачивать назад в оси снимка — это
 * не «тот же жест с другими параметрами», а другой жест.
 */
export function usePhotoZoomGestures({
  viewportRef,
  apply,
  panEnabled = true,
  zoomEnabled = true,
  onDoubleTap,
  onSingleTap,
}: PhotoZoomGestureOptions): ComposedGesture {
  /**
   * Состояние на момент начала жеста. Один ref на щипок и перетаскивание — они идут
   * одновременно и должны считать смещение от одной и той же отправной точки.
   */
  const startRef = useRef<PhotoZoomViewport | null>(null);

  /**
   * Сами обработчики — постоянные функции, которые при вызове видят свежие `apply` и колбэки:
   * жест собирается при рендере, а читать ref-ы ему можно только тогда, когда палец уже на
   * экране. Пересобирать жест ради нового `apply` при этом незачем.
   */
  const beginMove = useEventCallback(() => {
    startRef.current = viewportRef.current;
  });
  const panTo = useEventCallback((translationX: number, translationY: number) => {
    const start = startRef.current ?? viewportRef.current;
    apply({ ...start, tx: start.tx + translationX, ty: start.ty + translationY });
  });
  const pinchTo = useEventCallback((scale: number) => {
    const start = startRef.current ?? viewportRef.current;
    apply({ ...start, scale: start.scale * scale });
  });
  const doubleTapNow = useEventCallback(() => {
    if (onDoubleTap) apply(onDoubleTap(viewportRef.current));
  });
  const singleTapNow = useEventCallback(() => {
    if (onSingleTap && viewportRef.current.scale <= 1.01) onSingleTap();
  });
  const hasDoubleTap = Boolean(onDoubleTap);
  const hasSingleTap = Boolean(onSingleTap);

  const pan = useMemo(
    () =>
      Gesture.Pan()
        .enabled(panEnabled)
        .onBegin(() => beginMove())
        .onUpdate((e) => panTo(e.translationX, e.translationY))
        .runOnJS(true),
    [beginMove, panEnabled, panTo],
  );

  const pinch = useMemo(
    () =>
      Gesture.Pinch()
        .enabled(zoomEnabled)
        .onBegin(() => beginMove())
        .onUpdate((e) => pinchTo(e.scale))
        .runOnJS(true),
    [beginMove, pinchTo, zoomEnabled],
  );

  const doubleTap = useMemo(() => {
    if (!hasDoubleTap) return null;
    return Gesture.Tap()
      .numberOfTaps(2)
      .enabled(zoomEnabled)
      .onEnd(() => doubleTapNow())
      .runOnJS(true);
  }, [doubleTapNow, hasDoubleTap, zoomEnabled]);

  /**
   * Одиночное касание работает только у вписанного снимка: у приближённого это почти всегда
   * промах по перетаскиванию, и закрывать просмотр по нему обидно.
   */
  const singleTap = useMemo(() => {
    if (!hasSingleTap) return null;
    return Gesture.Tap()
      .numberOfTaps(1)
      .onEnd(() => singleTapNow())
      .runOnJS(true);
  }, [hasSingleTap, singleTapNow]);

  return useMemo(() => {
    const move = Gesture.Simultaneous(pan, pinch);
    if (doubleTap && singleTap) {
      return Gesture.Race(Gesture.Exclusive(doubleTap, singleTap), move);
    }
    if (doubleTap) return Gesture.Race(doubleTap, move);
    if (singleTap) return Gesture.Race(singleTap, move);
    return move;
  }, [doubleTap, pan, pinch, singleTap]);
}
